import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, X, Crown, BookOpen, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { SectionLabel, Pill, Spinner } from "../components/Primitives";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async (term) => {
    if (term.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/search", { params: { q: term } });
      setResults(res.data);
    } catch {
      setResults({ lessons: [], kingdoms: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search + keep URL in sync.
  useEffect(() => {
    const t = setTimeout(() => {
      setParams(q.trim() ? { q: q.trim() } : {}, { replace: true });
      runSearch(q);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div data-testid="search-page" className="mx-auto max-w-5xl px-6 py-16 md:px-12">
      <SectionLabel>Search</SectionLabel>
      <h1 className="font-serif text-5xl font-semibold text-bone lg:text-6xl">Find a kingdom or theme</h1>
      <p className="mt-4 text-muted">Search across every lesson and kingdom — including the full text of each article.</p>

      <div className="relative mt-8">
        <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. Sundiata, Timbuktu, colonialism, gold trade…"
          data-testid="global-search-input"
          className="w-full border border-edge bg-ink py-4 pl-12 pr-11 text-lg text-bone placeholder:text-muted focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-gold">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {loading && <Spinner />}

      {!loading && results && (
        <div className="mt-10" data-testid="search-results">
          <p className="mb-8 font-mono text-[11px] uppercase tracking-[0.15em] text-muted">
            {results.total} {results.total === 1 ? "result" : "results"} for “{results.query}”
          </p>

          {results.total === 0 && (
            <p className="border border-dashed border-edge p-12 text-center text-muted" data-testid="search-empty">
              No matches. Try a different term — a kingdom, person, place, or theme.
            </p>
          )}

          {results.kingdoms?.length > 0 && (
            <section className="mb-12">
              <h2 className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                <Crown className="h-4 w-4" /> Kingdoms ({results.kingdoms.length})
              </h2>
              <div className="space-y-3">
                {results.kingdoms.map((k) => (
                  <Link key={k.slug} to={`/kingdoms/${k.slug}`} data-testid={`search-kingdom-${k.slug}`} className="group flex items-start gap-4 border border-edge bg-ash p-5 transition-colors hover:border-gold/50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-serif text-xl text-bone group-hover:text-gold">{k.name}</h3>
                        {k.region && <Pill tone="muted">{k.region}</Pill>}
                      </div>
                      {k.snippet && <p className="mt-1 text-sm text-muted">{k.snippet}</p>}
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-gold transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {results.lessons?.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                <BookOpen className="h-4 w-4" /> Lessons ({results.lessons.length})
              </h2>
              <div className="space-y-3">
                {results.lessons.map((l) => (
                  <Link key={l.slug} to={`/lessons/${l.slug}`} data-testid={`search-lesson-${l.slug}`} className="group flex items-start gap-4 border border-edge bg-ash p-5 transition-colors hover:border-gold/50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-serif text-xl text-bone group-hover:text-gold">{l.title}</h3>
                        {l.category && <Pill tone="gold">{l.category}</Pill>}
                      </div>
                      <p className="mt-1 text-sm text-muted">{l.snippet || l.subtitle}</p>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-gold transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
